import { DatePipe, NgClass } from '@angular/common';
import { Component, computed, input, linkedSignal, model, output } from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { NgIcon } from '@ng-icons/core';
import { addMonths, eachDayOfInterval, endOfWeek } from 'date-fns';
import { Button } from '../button/button';
import { CalendarRange, fromDateString, toDateString } from './definitions';
import { DateString, getSixWeekRange, getWeekDays } from '../calendar-input/definitions';

@Component({
  selector: 'ui-calendar-range-input',
  imports: [DatePipe, Button, NgIcon, NgClass],
  templateUrl: './calendar-range-input.html',
})
export class CalendarRangeInput implements FormValueControl<CalendarRange> {
  readonly value = model<CalendarRange>({ start: null, end: null });
  readonly disabled = input<boolean>(false);
  readonly touched = model<boolean>(false);

  fieldValueChange = output<CalendarRange>();

  month = linkedSignal<{ value: CalendarRange }, DateString>({
    source: () => ({
      value: this.value(),
    }),
    computation: ({ value }, previous) => {
      if (previous?.value) {
        return previous.value;
      }
      // check if initial month needs to be adjusted to see the range
      const { start, end } = value;

      const currentWeekRange = getSixWeekRange(new Date());
      const isRangeInCurrentMonth = currentWeekRange
        .map((day) => toDateString(day))
        .some((dayStr) => dayStr === start || dayStr === end);

      if (isRangeInCurrentMonth) {
        return toDateString(new Date());
      }
      return start !== null ? start : end !== null ? end : toDateString(new Date());
    },
  });

  protected weekDays = computed(getWeekDays);

  private weekRange = computed(() => getSixWeekRange(this.month()));

  protected weeksOfMonth = computed<
    {
      date: DateString;
      status: 'start' | 'end' | 'startAndEnd' | 'selected' | 'inRange' | 'outOfRange';
    }[][]
  >(() => {
    const month = this.month();
    const weeks = this.weekRange();

    const currentMonth = fromDateString(month).getMonth();
    const { start, end } = this.value();
    const startDate = start !== null ? fromDateString(start) : null;
    const endDate = end !== null ? fromDateString(end) : null;

    return weeks.map((week) =>
      eachDayOfInterval({
        start: week,
        end: endOfWeek(week, { weekStartsOn: 1 }),
      }).map((nativeDate) => {
        const date = toDateString(nativeDate);
        if (date === start && date === end) {
          return { date, status: 'startAndEnd' };
        } else if (date === start) {
          return { date, status: 'start' };
        } else if (date === end) {
          return { date, status: 'end' };
        } else if (
          startDate !== null &&
          endDate !== null &&
          nativeDate > startDate &&
          nativeDate < endDate
        ) {
          return { date, status: 'selected' };
        } else if (nativeDate.getMonth() === currentMonth) {
          return { date, status: 'inRange' };
        } else {
          return { date, status: 'outOfRange' };
        }
      }),
    );
  });

  onClickNext() {
    this.month.set(toDateString(addMonths(this.month(), 1)));
  }

  onClickPrevious() {
    this.month.set(toDateString(addMonths(this.month(), -1)));
  }

  protected onClickDay(date: DateString) {
    this.touched.set(true);
    const nativeDate = fromDateString(date);
    this.value.update((value) => {
      if (value.start !== null && value.end === null) {
        if (nativeDate < fromDateString(value.start)) {
          // start and end are reversed, new start is new end and vice versa
          return { start: date, end: value.start };
        }
        return { start: value.start, end: date };
      }
      return { start: date, end: null };
    });
    this.fieldValueChange.emit(this.value());
  }
}
